import { throwFriendlyError } from '@/functional/friendlyErrors'
import { getSettingsDataToSave } from '@/functional/settings'
import { useStore } from 'vuex'
import { useApolloClient, useMutation } from '@vue/apollo-composable'
import {
  GetSettingsDocument,
  SaveSettingDocument,
} from '../../generated/graphql'
import type { SettingsToPersist } from '@/store/plugins/settingsConfig'
import { isNil, cloneDeep, isEqual } from 'lodash-es'
import { computed, inject, ref } from 'vue'
import deepmerge from 'deepmerge'
import { settingsToPersist } from '@/store/plugins/settingsConfig'
import { urlParamsToPersist } from '@/store/plugins/settingsConfig'
import { useRoute, useRouter } from 'vue-router'
import * as v from 'valibot'
import { isStorybookKey } from '@/utilities/inject'
import { FEATURE_FLAG } from '@/constants/featureFlags'

const persistedSettingsSchema = v.intersect([
  v.object({
    'request.sendCopyToMe': v.optional(v.boolean()),
  }),
  v.record(v.string(), v.any()),
])

type PersistedSettings = v.InferOutput<typeof persistedSettingsSchema>

const urlSettingsSchema = v.intersect([v.record(v.string(), v.any())])
type UrlSettings = v.InferOutput<typeof urlSettingsSchema>

const settingsSchema = v.intersect([persistedSettingsSchema, urlSettingsSchema])
type Settings = v.InferOutput<typeof settingsSchema>

/** Settings which are saved in the database */
const persistedSettings = ref<Settings>({})
/** Settings which are saved as url parameters */
const urlSettings = ref<Settings>({})

const settings = computed(() => {
  return deepmerge(persistedSettings.value, urlSettings.value)
})

export function useUserSettings() {
  const store = useStore()
  const isStorybook = inject(isStorybookKey)
  const route = useRoute()
  const router = useRouter()

  const { client: apolloClient } = useApolloClient()
  const saveSettingsMutation = useMutation(SaveSettingDocument)

  async function load() {
    const getSettingsQuery = await apolloClient.query({
      query: GetSettingsDocument,
      fetchPolicy: 'network-only',
    })

    if (isNil(getSettingsQuery.data.settings)) return

    const persoplanSettings = getSettingsQuery.data.settings.find((setting) => {
      if (!setting) return
      return setting.name === 'persoplan'
    })

    updateSettings(persoplanSettings?.value)
    return settings.value
  }

  function updateSettings(newPersistedSettings: any) {
    persistedSettings.value = getPersistedSettings(newPersistedSettings)
    urlSettings.value = getUrlSettings()
  }

  async function save(newSettings: Partial<SettingsToPersist>) {
    const settings = getSettingsDataToSave(newSettings)

    try {
      await saveSettingsMutation.mutate({
        values: JSON.stringify(settings),
      })

      store.commit('applyPersistedSettings', settings)
      updateSettings(JSON.stringify(settings))
    } catch (error) {
      throwFriendlyError(error)
    }
  }

  /**
   * @param value New value for `request.sendCopyToMe`
   */
  async function maybeSaveSendCopyToMe(value: boolean) {
    if (FEATURE_FLAG.SEND_COPY_TO_ME !== true) return

    const hasChanged = settings.value['request.sendCopyToMe'] !== value
    if (!hasChanged) return
    await save({ 'request.sendCopyToMe': value })
  }

  function getUrlSettings() {
    if (isStorybook) return {}

    const query = route.query

    // Find all items in url query, which have no counterpart in the settings
    const orphanedQueryIds = Object.keys(query).filter((id) => {
      const index = Object.values(urlParamsToPersist).findIndex(
        (item) => item.id === id
      )

      if (index === -1) return true
      return false
    })

    /**
     * Replace URL-friendly keys with actual statePaths
     */
    const mappedQuery = {}

    Object.entries(query).forEach(([id, value]) => {
      // If it's an orphaned item we skip it
      if (orphanedQueryIds.includes(id)) {
        return
      }

      const settingKey = Object.keys(urlParamsToPersist).find((key) => {
        const value = urlParamsToPersist[key]
        return value.id === id
      })

      if (!settingKey) throw new Error('settingKey not found')

      mappedQuery[settingKey] = value
    })

    // Remove unreferenced settings from url
    const newQuery = cloneDeep(query)
    Object.keys(newQuery).forEach((key) => {
      if (!orphanedQueryIds.includes(key)) return
      delete newQuery[key]
    })

    // Only update url if anything has changed
    const isSameQuery = isEqual(route.query, newQuery)
    if (!isSameQuery) {
      router.replace({ query: newQuery })
    }

    // Parse and apply settings
    const parsedSettings = parseSettings({
      values: mappedQuery,
      settings: urlParamsToPersist,
    })

    return parsedSettings
  }

  return {
    settings,
    load,
    save,
    maybeSaveSendCopyToMe,
  }
}

function getPersistedSettings(values) {
  if (isNil(values)) return

  const parsedSettings = parseSettings({
    values: JSON.parse(values),
    settings: settingsToPersist,
  })

  removeUnreferencedSettings({
    settings: parsedSettings,
    options: settingsToPersist,
  })

  const hasSettingsToApply = Object.keys(parsedSettings).length > 0
  if (!hasSettingsToApply) return

  return parsedSettings
}

export function parseSettings({ values, settings }) {
  const result = cloneDeep(values)

  Object.entries(result).forEach(([key, value]) => {
    const type = settings[key]?.type
    if (!type) return

    if (type === Date) {
      result[key] = new Date(value as any) // TODO: Improve types
      return
    }

    if (type === Number) {
      result[key] = parseInt(value as any) // TODO: Improve types
    }
  })

  return result
}

function removeUnreferencedSettings({ settings, options }) {
  const allowedSettings = Object.keys(options)

  const idsOfRemovedSettings: string[] = []

  Object.keys(settings).forEach((statePath) => {
    const isAllowed = allowedSettings.includes(statePath)
    if (isAllowed) return

    delete settings[statePath]
    idsOfRemovedSettings.push(statePath)
  })

  return idsOfRemovedSettings
}
